/** Eslatma qo'shish oynasi: driver profili va o'ng tugma menyusi uchun umumiy. Vaqt driver shtati bo'yicha kiritiladi. */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { dayjs } from "@/lib/utils";
import { useMe } from "@/features/auth/hooks";
import { useCreateReminder, useUserOptions } from "@/features/drivers/api";
import type { ReminderKind } from "@/features/drivers/types";
import { tzAbbr } from "@/features/drivers/timezones";
import { Button } from "@/components/ui/button";
import { Field, Input, Select, TimeInput } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";

export function AddReminderModal({ driverId, dTz, open, onOpenChange, defaultAssignee }: { driverId: number; dTz: string; open: boolean; onOpenChange: (o: boolean) => void; defaultAssignee: number | null }) {
  const { t } = useTranslation("drivers");
  const { t: tc } = useTranslation();
  const me = useMe().data;
  const create = useCreateReminder(driverId);
  const assignees = useUserOptions({}, open && !!me && me.role !== "recruiter");
  const [kind, setKind] = useState<ReminderKind>("call_back");
  const [date, setDate] = useState(dayjs().add(1, "day").format("YYYY-MM-DD"));
  const [time, setTime] = useState("09:00");
  const [note, setNote] = useState("");
  const [assignee, setAssignee] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setKind("call_back");
      setDate(dayjs().add(1, "day").format("YYYY-MM-DD"));
      setTime("09:00");
      setNote("");
      setAssignee(defaultAssignee ? String(defaultAssignee) : "");
      setError(null);
    }
  }, [open, defaultAssignee]);

  async function submit() {
    if (!date || !time) {
      setError(t("profile.reminder.chooseDateTime"));
      return;
    }
    // Vaqt driverning shtati bo'yicha kiritiladi (qo'ng'iroq driver vaqtida kelishiladi)
    let due: string;
    try {
      due = dayjs.tz(`${date}T${time}`, dTz).toISOString();
    } catch {
      due = dayjs(`${date}T${time}`).toISOString();
    }
    try {
      await create.mutateAsync({ kind, due_at: due, note, assigned_to_id: assignee ? Number(assignee) : undefined });
      toast.success(t("profile.reminder.added"));
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("profile.reminder.failed"));
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t("profile.reminder.title")}
      size="sm"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{tc("actions.cancel")}</Button>
          <Button variant="primary" onClick={submit} loading={create.isPending}>
            {tc("actions.add")}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <div className="rounded-[10px] border border-danger bg-danger-soft px-3 py-2 text-[12px] text-danger">{error}</div>}
        <Field label={t("profile.reminder.type")} htmlFor="rem-kind">
          <Select id="rem-kind" value={kind} onChange={(e) => setKind(e.target.value as ReminderKind)}>
            <option value="call_back">{tc("reminder.call_back")}</option>
            <option value="send_docs">{tc("reminder.send_docs")}</option>
            <option value="follow_up">{tc("reminder.follow_up")}</option>
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("profile.reminder.date")} htmlFor="rem-date">
            <Input id="rem-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label={t("profile.reminder.time")} htmlFor="rem-time" hint={t("profile.reminder.timeHint", { tz: tzAbbr(dTz, new Date()) })}>
            <TimeInput id="rem-time" value={time} onChange={(e) => setTime(e.target.value)} className="w-full" />
          </Field>
        </div>
        {me && me.role !== "recruiter" && (
          <Field label={t("profile.reminder.assignTo")} htmlFor="rem-assignee">
            <Select id="rem-assignee" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
              <option value="">{t("profile.reminder.driversOwner")}</option>
              {(assignees.data?.results ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label={t("profile.reminder.note")} htmlFor="rem-note">
          <Input id="rem-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("profile.reminder.notePlaceholder")} maxLength={200} />
        </Field>
      </div>
    </Modal>
  );
}

// ---- Tarix ----
